import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import MuiSidebar from '../../components/MuiSidebar';
import { FaArrowUp } from 'react-icons/fa';
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  TextField,
  Select,
  MenuItem,
  Grid,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import SessionExpiredPopup from '../../components/SessionExpiredPopup/SessionExpiredPopup';
import useSessionExpired from '../../hooks/useSessionExpired';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../assets/Logo.png';
import LogoW from '../../assets/LogoW.png'; // <-- new import
import ProfileSkeleton from '../../components/ProfileSkeleton';

/* ===================== styled ===================== */

const MainContent = styled(Box)(({ theme, open }) => ({
  flexGrow: 1,
  minHeight: '100vh',
  overflow: 'auto',
  padding: theme.spacing(3),
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  position: 'relative',
  zIndex: 1,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  backdropFilter: 'blur(10px)',
  [theme.breakpoints.up('md')]: {
    marginLeft: 0,
    width: '100%',
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(8),
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(8),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.85),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(3),
  transition: 'all 0.25s ease-in-out',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
  // increase space between title and content (help with textbox spacing)
  '& .MuiTypography-h6': {
    marginBottom: theme.spacing(3), // was spacing(2)
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: 40,
      height: 3,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    }
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.25,
  background: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  gap: theme.spacing(1),
  minHeight: 96,
  width: '100%',
  boxSizing: 'border-box',
  '& .MuiTypography-body2': {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  '& .MuiTypography-h5': {
    fontWeight: 800,
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
    },
  },
  '& .MuiChip-root': {
    marginTop: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    minHeight: 80,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: 'blur(5px)',
    transition: 'all 0.3s ease',
    borderRadius: theme.shape.borderRadius,
    '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.8) },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': { color: theme.palette.primary.main }
  }
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  '& img': { width: 50, height: 50 },
  '& .logo-text': {
    display: 'flex',
    flexDirection: 'column',
    '& .company-name': {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    '& .page-title': {
      fontSize: '1rem',
      color: theme.palette.text.secondary,
    },
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: { padding: theme.spacing(4) },
}));

const TransactionsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const TxnRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto auto',
  gap: theme.spacing(2),
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`
}));

/* ===================== helpers ===================== */

const fmtNum = (n, currency = '') => {
  const x = Number(n ?? 0);
  const pretty = Number.isFinite(x) ? x.toLocaleString() : '0';
  return currency ? `${pretty} ${currency}` : pretty;
};

const safe = (v, fallback = '') => (v ?? fallback);

const phoneIsValid = (p) => {
  if (!p || !p.trim()) return false;
  // allow digits, +, spaces, parentheses, dashes
  return /^[\d+\s()\-]+$/.test(p.trim());
};

/* ---------- Transaction table ---------- */
const statusToColor = (status) => {
  if (!status) return 'default';
  const s = String(status).toLowerCase();
  if (s.includes('done') || s.includes('completed') || s.includes('success') || s.includes('paid')) return 'success';
  if (s.includes('withdraw') || s.includes('rejected') || s.includes('failed')) return 'error';
  return 'default';
};

const stableSort = (array, comparator) => {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
};

const getComparator = (order, orderBy) => {
  return (a, b) => {
    const av = (a[orderBy] ?? '').toString().toLowerCase();
    const bv = (b[orderBy] ?? '').toString().toLowerCase();
    if (!isNaN(Number(a[orderBy])) && !isNaN(Number(b[orderBy]))) {
      return order === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
    }
    if (av < bv) return order === 'asc' ? -1 : 1;
    if (av > bv) return order === 'asc' ? 1 : -1;
    return 0;
  };
};

const TransactionTable = ({ txns = [], currency = 'USD', onLoadMore, loadingMore }) => {
  const theme = useTheme();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('transaction_date');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // apply sorting and pagination
  const sorted = useMemo(() => stableSort(txns.slice(), getComparator(order, orderBy)), [txns, order, orderBy]);
  const visible = useMemo(() => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sorted, page, rowsPerPage]);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return String(d); }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text));
      // lightweight visual feedback can be shown by parent toast if desired
    } catch { /* ignore */ }
  };

  return (
    <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small" aria-label="Recent transactions table">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'transaction_type' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'transaction_type'}
                  direction={orderBy === 'transaction_type' ? order : 'desc'}
                  onClick={() => handleRequestSort('transaction_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sortDirection={orderBy === 'amount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={orderBy === 'amount' ? order : 'desc'}
                  onClick={() => handleRequestSort('amount')}
                >
                  Amount ({currency})
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'transaction_date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'transaction_date'}
                  direction={orderBy === 'transaction_date' ? order : 'desc'}
                  onClick={() => handleRequestSort('transaction_date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visible.length === 0 && txns.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No recent transactions.
                </TableCell>
              </TableRow>
            )}

            {visible.map((t) => (
              <TableRow hover key={t.id}>
                <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                  {t.transaction_type || '—'}
                </TableCell>

                <TableCell>
                  <Chip
                    label={t.status || '—'}
                    size="small"
                    color={statusToColor(t.status)}
                    icon={String(t.status).toLowerCase().includes('pending') ? <PendingIcon fontSize="small" /> : undefined}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>

                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtNum(t.amount, currency)}
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary">{formatDate(t.transaction_date)}</Typography>
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="Copy transaction id">
                    <IconButton size="small" onClick={() => copyToClipboard(t.id)} aria-label={`Copy id ${t.id}`}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* <Tooltip title="View details">
                    <IconButton size="small" onClick={() => window.open(t.detail_url || '#', '_blank')} aria-label={`Open ${t.id}`}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip> */}
                </TableCell>
              </TableRow>
            ))}

            {/* Loading indicator on bottom row when more being fetched */}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 1 }}>
        <TablePagination
          component="div"
          count={txns.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 6, 10, 20]}
          labelRowsPerPage="Rows"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          sx={{
            '& .MuiTablePagination-toolbar': {
              display: 'flex',
              alignItems: 'center',        // ✅ vertical alignment fix
              justifyContent: 'space-between',
              flexWrap: 'nowrap',          // prevents wrapping on smaller screens
              minHeight: '40px',
              gap: 1,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              whiteSpace: 'nowrap',
              margin: 0,                   // removes weird vertical offsets
              lineHeight: 1.5,
            },
            '& .MuiTablePagination-select': {
              paddingTop: 0.25,
              paddingBottom: 0.25,
            },
            '& .MuiTablePagination-actions': {
              marginTop: 0,                // ✅ keeps arrows aligned with text
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />


        <Box>
          {typeof onLoadMore === 'function' && (
            <Tooltip title="Fetch next page from server">
              <Button size="small" onClick={onLoadMore} disabled={loadingMore}>Load More</Button>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

/* ===================== component ===================== */

const Profile = () => {
  const navigate = useNavigate();
  const { isSessionExpired, handleSessionExpired, closeSessionExpired } = useSessionExpired();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toastOptions = useMemo(() => ({
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  }), []);

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [showGoTop, setShowGoTop] = useState(false);

  const [user, setUser] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    profilePicture: null,
    customId: '',
  });
  const [originalUser, setOriginalUser] = useState(user);

  const [metrics, setMetrics] = useState({
    deposits: 0,
    withdrawals: 0,
    equity: 0,
    equity_percentage: 0,
    total_profit: 0,
    profit_asset: 0,
    profit_operation: 0,
    currency: '',
  });

  const [txns, setTxns] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnHasMore, setTxnHasMore] = useState(false);

  const getValidToken = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || token.split('.').length !== 3) {
      handleSessionExpired();
      return null;
    }
    return token;
  }, [handleSessionExpired]);

  /* ---------- fetch profile ---------- */
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    const token = getValidToken();
    if (!token) return;

    try {
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const res = await axios.get(`${apiUrl}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // new shape { success, message, data: { user, financial_metrics, recent_transactions } }
      const root = res.data;
      const data = root?.data || root; // tolerate both

      const u = data?.user || {};
      const fm = data?.financial_metrics || {};
      const rt = data?.recent_transactions || {};

      setUser({
        fullName: safe(u.full_name),
        email: safe(u.email),
        phone: safe(u.phone_number),
        country: safe(u.country),
        profilePicture: safe(u.profile_picture_url, null),
        customId: safe(u.custom_id),
      });
      setOriginalUser({
        fullName: safe(u.full_name),
        email: safe(u.email),
        phone: safe(u.phone_number),
        country: safe(u.country),
        profilePicture: safe(u.profile_picture_url, null),
        customId: safe(u.custom_id),
      });

      setMetrics({
        deposits: Number(fm.deposits || 0),
        withdrawals: Number(fm.withdrawals || 0),
        equity: Number(fm.equity || 0),
        equity_percentage: Number(fm.equity_percentage || 0),
        total_profit: Number(fm.total_profit || 0),
        profit_asset: Number(fm.profit_asset || 0),
        profit_operation: Number(fm.profit_operation || 0),
        currency: fm.currency || 'USD',
      });

      const rtData = Array.isArray(rt?.data) ? rt.data : [];
      setTxns(rtData);
      setTxnPage(rt?.pagination?.current_page || 1);
      setTxnHasMore(Boolean(rt?.pagination?.has_more));
    } catch (error) {
      console.error('Fetch profile error:', error);
      toast.error('Error fetching profile data.', toastOptions);
      if (error.response?.status === 401) handleSessionExpired();
    } finally {
      setLoading(false);
    }
  }, [getValidToken, handleSessionExpired, toastOptions]);

  const loadMoreTransactions = useCallback(async () => {
    const token = getValidToken();
    if (!token || !txnHasMore) return;
    try {
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const nextPage = txnPage + 1;
      const res = await axios.get(`${apiUrl}/profile?transactions_page=${nextPage}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || res.data;
      const rt = data?.recent_transactions || {};
      const rtData = Array.isArray(rt?.data) ? rt.data : [];
      setTxns(prev => prev.concat(rtData));
      setTxnPage(rt?.pagination?.current_page || nextPage);
      setTxnHasMore(Boolean(rt?.pagination?.has_more));
    } catch (err) {
      console.error('Load more transactions error:', err);
      toast.error('Could not load more transactions.', toastOptions);
    }
  }, [getValidToken, txnHasMore, txnPage, toastOptions]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  /* ---------- scroll / layout ---------- */
  useEffect(() => {
    const handleScroll = () => setShowGoTop(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);

  /* ---------- form handlers ---------- */
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setUser(prev => ({ ...prev, country: value, phone: '' }));
    } else {
      setUser(prev => ({ ...prev, [name]: value }));
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  /* ---------- validation ---------- */
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const validateProfile = () => {
    const newErrors = {};
    if (!user.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(user.fullName)) {
      newErrors.fullName = 'Full name can only contain letters and spaces';
    }
    if (!user.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(user.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!user.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneIsValid(user.phone)) {
      newErrors.phone = 'Use digits, +, spaces, () or - only';
    }
    if (!user.country.trim()) {
      newErrors.country = 'Country is required';
    }
    return newErrors;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!passwordForm.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) newErrors.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 8) newErrors.newPassword = 'Password must be at least 8 characters';
    if (!passwordForm.confirmPassword) newErrors.confirmPassword = 'Please confirm your new password';
    else if (passwordForm.newPassword !== passwordForm.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    return newErrors;
  };

  /* ---------- save handlers ---------- */
  const handleSaveProfile = async () => {
    const newErrors = validateProfile();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      Object.values(newErrors).forEach(m => toast.error(m, toastOptions));
      return;
    }

    const token = getValidToken();
    if (!token) return;

    try {
      const payload = {
        full_name: user.fullName,
        phone_number: user.phone,
        country: user.country,
      };
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const res = await axios.put(`${apiUrl}/profile`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (res.status === 200) {
        toast.success('Profile updated successfully!', toastOptions);
        const u = res.data?.data?.user || res.data?.user || {};
        setUser(prev => ({
          ...prev,
          fullName: u.full_name ?? prev.fullName,
          email: u.email ?? prev.email,
          phone: u.phone_number ?? prev.phone,
          country: u.country ?? prev.country,
          profilePicture: u.profile_picture_url ?? prev.profilePicture,
          customId: u.custom_id ?? prev.customId,
        }));
        setOriginalUser({
          fullName: u.full_name ?? user.fullName,
          email: u.email ?? user.email,
          phone: u.phone_number ?? user.phone,
          country: u.country ?? user.country,
          profilePicture: u.profile_picture_url ?? user.profilePicture,
          customId: u.custom_id ?? user.customId,
        });
        setEditMode(false);
        setErrors({});
      } else {
        toast.error('Failed to update profile.', toastOptions);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      const msg = error.response?.data?.message || 'Failed to update profile';
      toast.error(msg, toastOptions);
      if (error.response?.status === 401) handleSessionExpired();
    }
  };

  const handleChangePassword = async () => {
    const newErrors = validatePassword();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      Object.values(newErrors).forEach(m => toast.error(m, toastOptions));
      return;
    }

    const token = getValidToken();
    if (!token) return;

    try {
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      // Using same endpoint as your code; if your backend uses /profile/password, update here.
      const payload = { password: passwordForm.newPassword.trim() };
      const res = await axios.put(`${apiUrl}/profile`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (res.status === 200) {
        toast.success('Password changed successfully!', toastOptions);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({});
      }
    } catch (error) {
      console.error('Password change error:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to change password';
      toast.error(msg, toastOptions);
      if (error.response?.status === 401) handleSessionExpired();
    }
  };

  /* ---------- view helpers ---------- */
  const handleGoTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const countries = [
    'Select Country',
    'Egypt',
    'United States',
    'United Kingdom',
    'Australia',
    'Germany',
    'France',
    'Other'
  ];

  const phonePlaceholders = {
    Egypt: '+20 1X XXX XXXX',
    'United States': '+1 (123) 456-7890',
    'United Kingdom': '+44 7XXX XXXXXX',
    Australia: '+61 4XX XXX XXX',
    Germany: '+49 1512 3456789',
    France: '+33 6 12 34 56 78',
    Other: 'Enter your phone number'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <AnimatedBackground />
        <MuiSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          variant={isMobile ? 'temporary' : 'permanent'}
        />
        <MainContent open={sidebarOpen}>
          <PageContainer>
            <ProfileSkeleton />
          </PageContainer>
        </MainContent>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AnimatedBackground />
      <MuiSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        variant={isMobile ? 'temporary' : 'permanent'}
      />
      <MainContent open={sidebarOpen}>
        <PageContainer>
          <LogoSection>
            <img src={theme.palette.mode === 'dark' ? LogoW : Logo} alt="PropQuant Logo" />
            <Box className="logo-text">
              <Typography className="company-name">PROPQUANT</Typography>
              <Typography className="page-title">Profile</Typography>
            </Box>
          </LogoSection>

          {/* ===== Metrics ===== */}
          <Container maxWidth="lg" sx={{ mb: 1, px: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard>
                  <Typography variant="body2" color="text.secondary">Deposits</Typography>
                  <Typography variant="h5" fontWeight={700}>{fmtNum(metrics.deposits, metrics.currency)}</Typography>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard>
                  <Typography variant="body2" color="text.secondary">Withdrawals</Typography>
                  <Typography variant="h5" fontWeight={700}>{fmtNum(metrics.withdrawals, metrics.currency)}</Typography>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard>
                  <Typography variant="body2" color="text.secondary">Equity</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" fontWeight={700}>{fmtNum(metrics.equity, metrics.currency)}</Typography>
                    <Chip size="small" label={`${Number(metrics.equity_percentage || 0).toFixed(0)}%`} color="primary" />
                  </Box>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard>
                  <Typography variant="body2" color="text.secondary">Total Profit</Typography>
                  <Typography variant="h5" fontWeight={700}>{fmtNum(metrics.total_profit, metrics.currency)}</Typography>
                  <Box display="flex" gap={2} mt={0.5}>
                    <Typography variant="caption" color="text.secondary">Asset: {fmtNum(metrics.profit_asset, metrics.currency)}</Typography>
                    <Typography variant="caption" color="text.secondary">Operation: {fmtNum(metrics.profit_operation, metrics.currency)}</Typography>
                  </Box>
                </MetricCard>
              </Grid>
            </Grid>
          </Container>

          {/* ===== Personal Information ===== */}
          <Container maxWidth="lg" sx={{ mb: 4, px: 0 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard elevation={0}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Personal Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={user.fullName}
                        onChange={handleProfileChange}
                        disabled={!editMode}
                        error={!!errors.fullName}
                        helperText={errors.fullName}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={user.email}
                        onChange={handleProfileChange}
                        disabled
                        error={!!errors.email}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={user.phone}
                        onChange={handleProfileChange}
                        disabled={!editMode}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        placeholder={phonePlaceholders[user.country] || 'Enter your phone number'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Select
                        fullWidth
                        name="country"
                        value={user.country || 'Select Country'}
                        onChange={handleProfileChange}
                        disabled={!editMode}
                        error={!!errors.country}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    {!!user.customId && (
                      <Grid item xs={12} md={6}>
                        <StyledTextField
                          fullWidth
                          label="Customer ID"
                          value={user.customId}
                          disabled
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {editMode ? (
                      <>
                        <StyledButton variant="contained" color="primary" onClick={handleSaveProfile}>
                          Save Changes
                        </StyledButton>
                        <StyledButton
                          variant="outlined"
                          onClick={() => {
                            setUser(originalUser);
                            setEditMode(false);
                            setErrors({});
                          }}
                        >
                          Cancel
                        </StyledButton>
                      </>
                    ) : (
                      <StyledButton variant="contained" onClick={() => {
                        setOriginalUser(user);
                        setEditMode(true);
                      }}>
                        Edit Profile
                      </StyledButton>
                    )}
                  </Box>
                </StyledCard>
              </Grid>

              {/* ===== Change Password & Recent Transactions ===== */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <StyledCard elevation={0}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Change Password
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          type="password"
                          label="Current Password"
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          error={!!errors.currentPassword}
                          helperText={errors.currentPassword}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          type="password"
                          label="New Password"
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          error={!!errors.newPassword}
                          helperText={errors.newPassword}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          type="password"
                          label="Confirm Password"
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <StyledButton
                        variant="contained"
                        onClick={handleChangePassword}
                        disabled={
                          !passwordForm.currentPassword ||
                          !passwordForm.newPassword ||
                          !passwordForm.confirmPassword
                        }
                      >
                        Change Password
                      </StyledButton>
                    </Box>
                  </StyledCard>

                  <StyledCard elevation={0}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" color="primary">Recent Transactions</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Using improved, sortable table */}
                    <TransactionTable
                      txns={txns}
                      currency={metrics.currency}
                      onLoadMore={txnHasMore ? loadMoreTransactions : undefined}
                      loadingMore={loading && txnHasMore}
                    />
                  </StyledCard>
                </Box>
              </Grid>
            </Grid>
          </Container>

          {showGoTop && (
            <StyledButton
              className="go-top-btn"
              onClick={handleGoTop}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                minWidth: 'auto',
                width: 48,
                height: 48,
                borderRadius: '50%',
              }}
            >
              <FaArrowUp />
            </StyledButton>
          )}
        </PageContainer>
      </MainContent>

      {/* keep prop name consistent with other pages */}
      <SessionExpiredPopup open={isSessionExpired} onClose={closeSessionExpired} />
      <ToastContainer position="top-right" autoClose={5000} />
    </Box>
  );
};

export default Profile;
